import logger from '@/logger';
import { get, set, del } from 'idb-keyval';

const log = logger.getChild('csv-map-storage');

type MapType = 'csv' | 'dnc';

interface MapStorageUtils {
  getStoredMap: (
    companyId: string,
    fields: string[],
  ) => Promise<Record<string, string> | undefined>;
  getStoredCustomMap: (
    companyId: string,
    fields: string[],
  ) => Promise<Record<string, string> | undefined>;
  storeMap: (
    companyId: string,
    fields: string[],
    csvMap: Record<string, string>,
  ) => Promise<void>;
  storeCustomMap: (
    companyId: string,
    fields: string[],
    csvMap: Record<string, string>,
  ) => Promise<void>;
  deleteMap: (companyId: string, fields: string[]) => Promise<void>;
  deleteCustomMap: (companyId: string, fields: string[]) => Promise<void>;
}

class MapStorage {
  private readonly storageUtils: Map<MapType, MapStorageUtils> = new Map();

  constructor() {
    this.storageUtils.set('csv', this.createStorageUtils(''));
    this.storageUtils.set('dnc', this.createStorageUtils('dnc'));
  }

  private createStorageUtils(prefix: string): MapStorageUtils {
    const getMapKey = (fields: string[]): string => {
      return prefix ? `${prefix}:${fields.join(',')}` : fields.join(',');
    };

    const getCustomMapKey = (fields: string[]): string => {
      return prefix
        ? `${prefix}:custom:${fields.join(',')}`
        : `custom:${fields.join(',')}`;
    };

    const name = prefix ? prefix.toUpperCase() : 'DP';
    const customName = prefix ? `${prefix.toUpperCase()} custom` : 'custom';

    return {
      getStoredMap: async (
        companyId: string,
        fields: string[],
      ): Promise<Record<string, string> | undefined> => {
        log.debug(`Getting stored ${name} map`, { fields });
        const map = await get<Record<string, string>>(
          `${companyId}:${getMapKey(fields)}`,
        );
        log.log(`Got stored ${name} map`, { map });
        return map;
      },

      getStoredCustomMap: async (
        companyId: string,
        fields: string[],
      ): Promise<Record<string, string> | undefined> => {
        log.debug(`Getting stored ${customName} map`, { fields });
        const map = await get<Record<string, string>>(
          `${companyId}:${getCustomMapKey(fields)}`,
        );
        log.log(`Got stored ${customName} map`, { map });
        return map;
      },

      storeMap: async (
        companyId: string,
        fields: string[],
        csvMap: Record<string, string>,
      ): Promise<void> => {
        log.log(`Storing ${name} map for ${fields}`, csvMap);
        const copy = JSON.parse(JSON.stringify(csvMap));
        await set(`${companyId}:${getMapKey(fields)}`, copy);
      },

      storeCustomMap: async (
        companyId: string,
        fields: string[],
        csvMap: Record<string, string>,
      ): Promise<void> => {
        log.log(`Storing ${customName} map for ${fields}`, csvMap);
        const copy = JSON.parse(JSON.stringify(csvMap));
        await set(`${companyId}:${getCustomMapKey(fields)}`, copy);
      },

      deleteMap: async (companyId: string, fields: string[]): Promise<void> => {
        log.log(`Deleting ${name} map`, { fields });
        await del(`${companyId}:${getMapKey(fields)}`);
      },

      deleteCustomMap: async (
        companyId: string,
        fields: string[],
      ): Promise<void> => {
        log.log(`Deleting ${customName} map`, { fields });
        await del(`${companyId}:${getCustomMapKey(fields)}`);
      },
    };
  }

  getUtils(type: MapType): MapStorageUtils {
    const utils = this.storageUtils.get(type);
    if (!utils) {
      throw new Error(`Unknown map type: ${type}`);
    }
    return utils;
  }
}

export const mapStorage = new MapStorage();

const csvUtils = mapStorage.getUtils('csv');
export const getStoredCsvMap = csvUtils.getStoredMap;
export const getStoredCsvCustomMap = csvUtils.getStoredCustomMap;
export const storeCsvMap = csvUtils.storeMap;
export const storeCsvCustomMap = csvUtils.storeCustomMap;
export const deleteCsvMap = csvUtils.deleteMap;
export const deleteCsvCustomMap = csvUtils.deleteCustomMap;

const dncUtils = mapStorage.getUtils('dnc');
export const getStoredDncMap = dncUtils.getStoredMap;
export const getStoredDncCustomMap = dncUtils.getStoredCustomMap;
export const storeDncMap = dncUtils.storeMap;
export const storeDncCustomMap = dncUtils.storeCustomMap;
export const deleteDncMap = dncUtils.deleteMap;
export const deleteDncCustomMap = dncUtils.deleteCustomMap;
